// src/components/session/SessionList.js - Fixed with direct name lookup
import React, { useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper,
  Chip,
  IconButton,
  Card,
  CardContent,
  Stack,
  Tooltip
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaymentIcon from '@mui/icons-material/Payment';
import BoltIcon from '@mui/icons-material/Bolt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoadingSpinner from '../common/Loadingspinner';
import ErrorAlert from '../common/ErrorAlert';
import { getChargers } from '../../api/chargerAPI';
import { getDrivers } from '../../api/driverAPI';

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString('en-GB');
  } catch {
    return dateString;
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'started':
      return 'primary';
    case 'failed':
    case 'error':
      return 'error';
    default:
      return 'default';
  }
};

// Helper function to determine if payment button should be shown
const shouldShowPaymentButton = (session) => {
  // Show payment button if:
  // 1. Session is completed (has an end time)
  // 2. Session has a cost greater than 0
  // 3. Session payment status is not already 'paid', 'completed', 'succeeded', or 'success'
  return session.ChargerSessionEnd && 
         session.ChargerSessionCost && 
         session.ChargerSessionCost > 0 &&
         (!session.ChargerSessionPaymentStatus || 
          !['paid', 'completed', 'succeeded', 'success'].includes(session.ChargerSessionPaymentStatus?.toLowerCase()));
};

const SessionList = ({ 
  sessions, 
  isLoading, 
  isError, 
  error,
  showCharger = true,
  showDriver = true 
}) => {
  // Fetch all chargers for lookup
  const { data: allChargers } = useQuery({
    queryKey: ['all-chargers'],
    queryFn: () => getChargers({})
  });

  // Fetch all drivers for lookup
  const { data: allDrivers } = useQuery({
    queryKey: ['all-drivers'],
    queryFn: () => getDrivers()
  });

  // Create lookup maps for charger ID -> name and driver ID -> name
  const chargerLookup = useMemo(() => {
    if (!allChargers) return {};
    const lookup = {};
    allChargers.forEach(charger => {
      lookup[charger.ChargerId] = charger.ChargerName;
    });
    console.log('Charger lookup created:', lookup); // Debug log
    return lookup;
  }, [allChargers]);

  const driverLookup = useMemo(() => {
    if (!allDrivers) return {};
    const lookup = {};
    allDrivers.forEach(driver => {
      lookup[driver.DriverId] = {
        name: driver.DriverFullName,
        email: driver.DriverEmail
      };
    });
    console.log('Driver lookup created:', lookup); // Debug log
    return lookup;
  }, [allDrivers]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading sessions'} />;
  }

  if (!sessions || sessions.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" color="text.secondary">
          No charging sessions found
        </Typography>
      </Box>
    );
  }

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent sx={{ padding: 0 }}>
        <TableContainer component={Paper} sx={{ maxHeight: 650, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="120px">Session ID</TableCell>
                {showCharger && <TableCell width="180px">Charger Name</TableCell>}
                <TableCell width="100px">Connector</TableCell>
                {showDriver && <TableCell width="180px">Driver Name</TableCell>}
                <TableCell width="180px">Start Time</TableCell>
                <TableCell width="180px">End Time</TableCell>
                <TableCell width="120px">Duration</TableCell>
                <TableCell width="120px">Energy</TableCell>
                <TableCell width="100px">Cost</TableCell>
                <TableCell width="100px">Status</TableCell>
                <TableCell width="140px" align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => {
                // Get charger and driver names using lookup
                const chargerName = chargerLookup[session.ChargerSessionChargerId] || `Charger ${session.ChargerSessionChargerId}`;
                const driverData = session.ChargerSessionDriverId ? 
                  (driverLookup[session.ChargerSessionDriverId] || { name: `Driver ${session.ChargerSessionDriverId}`, email: null }) : 
                  null;

                console.log(`Session ${session.ChargeSessionId}: Charger ID ${session.ChargerSessionChargerId} -> ${chargerName}`); // Debug log
                if (session.ChargerSessionDriverId) {
                  console.log(`Session ${session.ChargeSessionId}: Driver ID ${session.ChargerSessionDriverId} -> ${driverData?.name}`); // Debug log
                }

                return (
                  <TableRow key={session.ChargeSessionId}>
                    <TableCell>#{session.ChargeSessionId}</TableCell>
                    
                    {showCharger && (
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {chargerName}
                        </Typography>
                      </TableCell>
                    )}
                    
                    <TableCell>
                      <Chip 
                        label={`#${session.ChargerSessionConnectorId}`} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    
                    {showDriver && (
                      <TableCell>
                        {session.ChargerSessionDriverId ? (
                          <Box>
                            <Typography variant="body2" noWrap>
                              {driverData.name}
                            </Typography>
                            {driverData.email && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {driverData.email}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No driver info
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                        {formatDateTime(session.ChargerSessionStart)}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      {session.ChargerSessionEnd ? formatDateTime(session.ChargerSessionEnd) : '-'}
                    </TableCell>
                    
                    <TableCell>
                      {formatDuration(session.ChargerSessionDuration)}
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <BoltIcon fontSize="small" sx={{ mr: 0.5, color: 'warning.main' }} />
                        {session.ChargerSessionEnergyKWH ? 
                          `${session.ChargerSessionEnergyKWH.toFixed(2)} kWh` : 
                          '-'
                        }
                      </Box>
                    </TableCell>

                    <TableCell>
                      {session.ChargerSessionCost ? 
                        `$${session.ChargerSessionCost.toFixed(2)}` : 
                        '-'
                      }
                    </TableCell>
                    
                    <TableCell>
                      <Chip 
                        label={session.ChargerSessionStatus || 'Unknown'} 
                        color={getStatusColor(session.ChargerSessionStatus)}
                        size="small"
                      />
                    </TableCell>
                    
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton 
                            component={Link} 
                            to={`/sessions/${session.ChargeSessionId}`}
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {/* {shouldShowPaymentButton(session) && (
                          <Tooltip title="Make Payment">
                            <IconButton
                              component={Link}
                              to={`/sessions/${session.ChargeSessionId}/payment`}
                              size="small"
                              sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'primary.dark'
                                }
                              }}
                            >
                              <PaymentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )} */}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default SessionList;